/**
 * Decoupe le texte colle par le visiteur en liste de groupes.
 *
 * Une ligne = un groupe. On retire ce qu'une note de telephone ou un message WhatsApp
 * ajoute autour d'un nom : puces (tirets, etoiles, puces rondes ou carrees), numerotation
 * (y compris en emojis touche), emojis, marqueurs de gras, caracteres invisibles ou de
 * controle. Une ligne vide apres nettoyage ne donne aucun groupe, pas plus qu'un intitule :
 * une ligne qui parle de line-up, de concerts ou de festivals, ou qui finit par « : ».
 * Les doublons se fusionnent sans tenir compte de la casse, avec un compteur : un groupe vu
 * deux fois grossit sur l'affiche.
 *
 * Tout se passe dans le navigateur : rien de ce texte ne part au serveur.
 */

/** Longueur maximale d'un nom, en caracteres. */
export const MAX_NAME_LENGTH = 60;
/** Nombre maximal de groupes retenus. */
export const MAX_BANDS = 300;

export interface Band {
  /** Cle de dedoublonnage : le nom en minuscules. */
  key: string;
  /** Le nom tel qu'il a ete saisi la premiere fois. */
  name: string;
  /** Nombre de lignes qui le citent. */
  count: number;
}

export interface ParseResult {
  bands: Band[];
  /** Vrai quand des groupes au-dela de MAX_BANDS ont ete ignores. */
  truncated: boolean;
}

// Moities de paire isolees (un emoji coupe en deux) et non-caracteres : ils font lever
// encodeURIComponent et rendent le SVG invalide.
const BROKEN = /\p{Cs}|\u{FFFE}|\u{FFFF}/gu;
// Caracteres de controle (sauf tabulation et fins de ligne) : invalides en XML.
const CONTROLS_EXCEPT_LINES = /(?![\t\n\r])\p{Cc}/gu;
const CONTROLS = /\p{Cc}/gu;
// Caracteres de format invisibles : espaces sans chasse, marques de sens, cesure molle.
const INVISIBLE = /\p{Cf}/gu;

// Emojis et pictogrammes, avec leurs modificateurs (teinte de peau, selecteur de variante,
// touche, drapeaux).
const EMOJI =
  /\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|\u{FE0E}|\u{FE0F}|\u{20E3}/gu;
// Numerotation en emojis touche : « 1️⃣ », « 1️⃣2️⃣ », « #️⃣ ».
const KEYCAP_NUMBERING = /^(?:\s*[0-9#*]\u{FE0F}?\u{20E3})+/u;

// Puces graphiques : toujours retirees en tete de ligne, collees au nom ou non.
const GLYPH_BULLETS =
  /^[\u{2022}\u{2023}\u{2043}\u{00B7}\u{25A0}-\u{25FF}\u{2190}-\u{21FF}\u{2794}-\u{27BF}\u{2219}\u{2013}\u{2014}\u{2212}>]+\s*/u;
// Puces de clavier : retirees seulement quand une espace les suit (« -M- » reste « -M- »).
const KEYBOARD_BULLETS = /^[-*+~]+\s+/u;
// Numerotation : « 1. », « 12) », « 3 - ». Ni « 30 Seconds to Mars » ni « 2:54 ».
const NUMBERING = /^\d{1,3}(?:\s*[.)\]]|\s*-\s)\s*/u;
// Marqueurs de gras, d'italique et de barre de WhatsApp.
const WHATSAPP_MARKERS = /[*~]|(?<=^|\s)_+|_+(?=\s|$)/gu;
// Un intitule : le mot line-up, concerts ou festivals, ou une ligne qui finit par « : ».
const HEADING_WORDS = /(?<![\p{L}\p{N}])(?:line[\s-]?up|concerts?|festivals?)(?![\p{L}\p{N}])/iu;
const HAS_CONTENT = /[\p{L}\p{N}]/u;
// Seul nom sans lettre ni chiffre accepte : le groupe « !!! ».
const PUNCTUATION_BANDS = new Set(["!!!"]);

/** Retire d'un texte entier ce qui casserait la page (garde les fins de ligne). */
export function sanitizeText(text: string): string {
  return text.replace(BROKEN, "").replace(CONTROLS_EXCEPT_LINES, "");
}

/** Retire d'une ligne seule (titre, nom) tout caractere casse, de controle ou invisible. */
export function sanitizeLine(text: string): string {
  return text.replace(BROKEN, "").replace(CONTROLS, " ").replace(INVISIBLE, "");
}

function isHeading(bare: string): boolean {
  return bare.endsWith(":") || HEADING_WORDS.test(bare);
}

function stripPrefixes(line: string): string {
  let current = line;
  let previous = "";
  while (current !== previous) {
    previous = current;
    current = current
      .replace(GLYPH_BULLETS, "")
      .replace(KEYBOARD_BULLETS, "")
      .replace(NUMBERING, "")
      .trimStart();
  }
  return current;
}

function truncate(name: string): string {
  const chars = Array.from(name);
  if (chars.length <= MAX_NAME_LENGTH) {
    return name;
  }
  return chars.slice(0, MAX_NAME_LENGTH).join("").trimEnd();
}

/** Nettoie une ligne ; rend une chaine vide quand la ligne ne porte aucun groupe. */
export function cleanLine(line: string): string {
  const safe = sanitizeLine(line).replace(KEYCAP_NUMBERING, " ");
  const bare = safe.replace(EMOJI, " ").replace(WHATSAPP_MARKERS, "").replace(/\s+/gu, " ").trim();
  if (isHeading(bare)) {
    return "";
  }
  const name = stripPrefixes(bare).trim();
  if (!HAS_CONTENT.test(name) && !PUNCTUATION_BANDS.has(name)) {
    return "";
  }
  return truncate(name);
}

/** Cle de dedoublonnage d'un nom. */
export function bandKey(name: string): string {
  return name.normalize("NFC").toLocaleLowerCase("fr-FR");
}

/** Decoupe un texte colle en groupes, dans l'ordre de leur premiere apparition. */
export function parseLineup(text: string): ParseResult {
  const byKey = new Map<string, Band>();
  let truncated = false;
  for (const line of text.split(/\r\n|[\n\r\v\f\u{85}\u{2028}\u{2029}]/u)) {
    const name = cleanLine(line);
    if (name === "") {
      continue;
    }
    const key = bandKey(name);
    const known = byKey.get(key);
    if (known) {
      known.count += 1;
    } else if (byKey.size < MAX_BANDS) {
      byKey.set(key, { key, name, count: 1 });
    } else {
      truncated = true;
    }
  }
  return { bands: [...byKey.values()], truncated };
}
